/**
 * Вознаграждение по результату: чтение и спор (BE-333).
 *
 * Расчёт **не запускается** отсюда: его единственный вход — отметка «купил» на
 * шаге 7 (§42.4). Спор необратим в пределах экрана и идёт без оптимистичного
 * UI (CLAUDE.md §8): ответ сервера и есть новое состояние.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@shared/api/client';
import { queryKeys } from '@shared/api/queryKeys';
import { randomUuid } from '@shared/lib/uuid';

import type { SuccessFee, SuccessFeeDisputeReason } from './types';

function base(searchId: string): string {
  return `/search-sessions/${encodeURIComponent(searchId)}/success-fee`;
}

/**
 * Расчёт по заказу.
 *
 * `404 SUCCESS_FEE_NOT_FOUND` — расчёта ещё нет: исход покупки не отмечен. Это
 * состояние, а не сбой, и экран просто не показывает блок.
 */
export function useSuccessFee(searchId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.successFee(searchId ?? ''),
    queryFn: () => apiFetch<SuccessFee>(base(searchId ?? '')),
    enabled: enabled && searchId !== null && searchId !== '',
    retry: false,
  });
}

export function useDisputeSuccessFee(searchId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reason: SuccessFeeDisputeReason) =>
      apiFetch<SuccessFee>(`${base(searchId)}/dispute`, {
        method: 'POST',
        idempotencyKey: randomUuid(),
        body: { reason },
      }),
    onSuccess: (fee: SuccessFee) => {
      queryClient.setQueryData(queryKeys.successFee(searchId), fee);
    },
  });
}
